// src/components/LoginPage/LoginPage.js

import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';
import { AuthContext } from '../../context/AuthContext';
import LoginFormFields from './LoginFormFields';
import AuthErrorBox from './AuthErrorBox';
import ToggleAuthMode from './ToggleAuthMode';

const API_URL = process.env.REACT_APP_API_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  // === 2FA additions ===
  const [step, setStep] = useState("login"); // 'login' or '2fa'
  const [pendingUserId, setPendingUserId] = useState(null);
  const [code, setCode] = useState("");

  const [form, setForm] = useState({
    email: '', password: '', firstName: '', lastName: '', organizationCode: ''
  });
  const [errors, setErrors] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  // --- LOGIN or SIGNUP SUBMIT ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setLoading(true);

    const endpoint = isCreating ? 'sign_up' : 'login';

    const payload = isCreating
      ? {
        email: form.email,
        password: form.password,
        first_name: form.firstName,
        last_name: form.lastName,
        organization_code: form.organizationCode,
      }
      : { email: form.email, password: form.password };

    try {
      const response = await fetch(`${API_URL}/auth/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      // === ALWAYS check errors first ===
      if (!response.ok) {
        if (Array.isArray(data.detail)) {
          setErrors(data.detail.map(err => err.msg));
        } else {
          setErrors([data.detail || 'Something went wrong']);
        }
        setLoading(false);
        return;
      }

      if (data.twofa_required) {
        setStep("2fa");
        setPendingUserId(data.user_id);
        setErrors([]);
        setLoading(false);
        return;
      }

      if (data.access_token) {
        login(data.access_token);
        setLoading(false);
        navigate('/');
        return;
      }

      setErrors(['Unknown response from server.']);
      setLoading(false);
    } catch {
      setLoading(false);
      setErrors(['An error occurred. Please try again.']);
    }
  };

  // --- 2FA SUBMIT ---
  const handle2faSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/verify_2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: pendingUserId, code })
      });
      const data = await response.json();

      if (!response.ok) {
        setErrors([data.detail || 'Invalid code']);
        setLoading(false);
        return;
      }
      // Successful: get access_token
      login(data.access_token);
      setLoading(false);
      navigate('/');
    } catch {
      setLoading(false);
      setErrors(['An error occurred. Please try again.']);
    }
  };

  // === RENDER ===
  return (
    <div className="login-container">
      <form className="login-form" onSubmit={step === "login" ? handleSubmit : handle2faSubmit}>
        <h2>
          {step === "login" ? (isCreating ? 'Create Account' : 'Login') : "Two-Factor Verification"}
        </h2>
        <AuthErrorBox errors={errors} />

        {/* MAIN LOGIN/SIGNUP FORM */}
        {step === "login" ? (
          <>
            <LoginFormFields
              form={form}
              isCreating={isCreating}
              onChange={handleChange}
            />
            <button type="submit" disabled={loading}>
              {isCreating ? 'Create Account' : 'Log In'}
            </button>
            <ToggleAuthMode isCreating={isCreating} setIsCreating={setIsCreating} />
          </>
        ) : (
          // 2FA CODE FORM
          <>
            <input
              type="text"
              placeholder="Enter 2FA Code"
              value={code}
              onChange={e => setCode(e.target.value)}
              required
              inputMode="numeric"
              style={{ letterSpacing: "0.2em", textAlign: "center", fontWeight: 600 }}
            />
            <button type="submit" disabled={loading}>
              Verify
            </button>
            <div style={{ marginTop: 18, color: "#666", fontSize: "0.95em" }}>
              Didn't get a code? Check your spam folder, or try logging in again.
            </div>
            <div style={{ marginTop: 12 }}>
              <button type="button" className="secondary-btn" onClick={() => { setStep("login"); setErrors([]); }}>
                ← Back to Login
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default LoginPage;
